/**
 * Parse a `--since` / `--until` value into an ISO 8601 timestamp.
 *
 * Accepts either:
 *   - Relative shorthand: `30s`, `15m`, `2h`, `1d` — interpreted as `now - duration`.
 *   - ISO 8601 string parseable by `Date.parse()`.
 *
 * Throws on unparseable input with a helpful message.
 */
export function parseTime(input: string, now: Date = new Date()): string {
  const trimmed = input.trim();
  if (!trimmed) {
    throw new Error(`Cannot parse empty time value`);
  }

  const relMatch = /^(\d+)\s*(s|m|h|d)$/i.exec(trimmed);
  if (relMatch) {
    const amount = Number.parseInt(relMatch[1]!, 10);
    const unit = relMatch[2]!.toLowerCase();
    const ms = amount * unitMs(unit);
    return new Date(now.getTime() - ms).toISOString();
  }

  const parsed = Date.parse(trimmed);
  if (!Number.isNaN(parsed)) {
    return new Date(parsed).toISOString();
  }

  throw new Error(
    `Unparseable time value: "${input}". Expected ISO 8601 (e.g. 2026-05-01T10:00:00Z) or a relative shorthand (30s, 15m, 2h, 1d).`,
  );
}

function unitMs(unit: string): number {
  switch (unit) {
    case "s":
      return 1000;
    case "m":
      return 60_000;
    case "h":
      return 3_600_000;
    case "d":
      return 86_400_000;
    default:
      throw new Error(`Unknown time unit: ${unit}`);
  }
}
